/**
 * W-09 "Sayım" — the pure half of the count screens: the list's filters and
 * summary line, `get_stock_count_review`'s jsonb read into something typed, which
 * lines a reviewer may select, and the four numbers the tiles, the footer and the
 * confirmation sheet all have to agree on.
 *
 * Counting itself happens on the phone (DECISIONS G12), so nothing here enters a
 * quantity: the web starts a session, reads what came back and approves it.
 *
 * Quantities arrive in **base** units (`private.snapshot_count_location` writes
 * `stock_balances.qty_base` into `expected_qty`) and are shown in the line's own
 * unit, so the division by `unit_factor` happens once, here, and no screen
 * divides by a factor of its own. Money is `value_impact_base`, which the RPC
 * leaves absent for a role without `seesCosts` — a masked cost stays `null` and
 * never becomes a zero.
 */
import { D, type Dec, type DecimalInput } from '../money';
import { MINUS, formatNumber } from '../format';

// ── statuses ────────────────────────────────────────────────────────────────
export const COUNT_STATUSES = ['draft', 'in_progress', 'submitted', 'recount_requested', 'approved', 'cancelled', 'reverted'] as const;
export type CountStatus = (typeof COUNT_STATUSES)[number];

export const COUNT_KINDS = ['cycle', 'full', 'opening'] as const;
export type CountKind = (typeof COUNT_KINDS)[number];

export type CountVarianceClass = 'equal' | 'within_tolerance' | 'over_tolerance' | 'uncounted';
export type CountLineResolution = 'pending' | 'adjusted' | 'ignored' | 'recount';
export type CountConflictStatus = 'none' | 'open' | 'kept' | 'recount';

/** Sessions that are still being counted — the "Açık" chip. */
export const COUNT_OPEN_STATUSES: CountStatus[] = ['draft', 'in_progress', 'recount_requested'];

export function parseCountStatus(raw: unknown): CountStatus | null {
  return (COUNT_STATUSES as readonly string[]).includes(raw as string) ? (raw as CountStatus) : null;
}

/** A count nobody can change any more: no buttons, no checkboxes (§6.6). */
export function isCountClosed(status: CountStatus | null | undefined): boolean {
  return status === 'approved' || status === 'cancelled' || status === 'reverted';
}

// ── list filters (W-09a) ────────────────────────────────────────────────────
export const COUNT_FILTERS = ['all', 'open', 'pending', 'approved'] as const;
export type CountFilter = (typeof COUNT_FILTERS)[number];

export function parseCountFilter(raw: unknown): CountFilter | undefined {
  return (COUNT_FILTERS as readonly string[]).includes(raw as string) ? (raw as CountFilter) : undefined;
}

export interface CountListRow {
  status?: CountStatus | string | null;
  warehouse_id?: string | null;
  warehouse_code?: string | null;
  location_codes?: (string | null)[] | null;
  submitted_at?: string | null;
  started_at?: string | null;
  updated_at?: string | null;
  lines_with_variance?: number | null;
}

export function matchesCountFilter(row: CountListRow, filter: CountFilter): boolean {
  const status = parseCountStatus(row.status);
  if (filter === 'all') return true;
  if (!status) return false;
  if (filter === 'open') return COUNT_OPEN_STATUSES.includes(status);
  if (filter === 'pending') return status === 'submitted';
  return status === 'approved';
}

/** "22 Eyl 14:06" comes from the submitted time when there is one, else the start. */
export function countListDate(row: CountListRow): string | null {
  return row.submitted_at ?? row.started_at ?? row.updated_at ?? null;
}

/**
 * The locations a session covers, as the banner and the list column write them:
 * one code on its own, more as "D1-B-03 +2" (§5, §6.2). Codes come from
 * `v_stock_counts.location_codes`, already in `seq` order.
 */
export function countLocationsLabel(codes: (string | null)[] | null | undefined): string {
  const list = (codes ?? []).filter((c): c is string => !!c);
  if (list.length === 0) return '';
  return list.length === 1 ? list[0]! : `${list[0]} +${list.length - 1}`;
}

/** The list's "Depo · lokasyon" cell: "D1 · D1-B-03 +2". */
export function countScopeLabel(row: CountListRow): string {
  return [row.warehouse_code, countLocationsLabel(row.location_codes)].filter(Boolean).join(' · ');
}

export interface CountListSummary {
  /** Sessions still being counted. */
  open: number;
  /** Sessions waiting for an approval. */
  pending: number;
  /** Newest activity across the list — what "son güncelleme" reads. */
  lastAt: string | null;
}

export function countListSummary(rows: CountListRow[]): CountListSummary {
  let open = 0;
  let pending = 0;
  let lastAt: string | null = null;
  for (const row of rows) {
    const status = parseCountStatus(row.status);
    if (status && COUNT_OPEN_STATUSES.includes(status)) open++;
    if (status === 'submitted') pending++;
    const at = countListDate(row);
    if (at && (!lastAt || at > lastAt)) lastAt = at;
  }
  return { open, pending, lastAt };
}

// ── review payload (`get_stock_count_review`) ───────────────────────────────
export interface CountReviewLine {
  id: string;
  itemId: string | null;
  sku: string | null;
  nameTr: string;
  attributes: unknown;
  lotId: string | null;
  lotNo: string | null;
  locationId: string | null;
  locationCode: string;
  /** The line's counting unit (the item's stock unit) and base per unit. */
  unit: string | null;
  unitFactor: number;
  /** Base-unit decimal strings, exactly as the RPC returns them. */
  expectedQty: string;
  countedQty: string | null;
  variance: string;
  variancePct: string | null;
  varianceClass: CountVarianceClass;
  resolution: CountLineResolution;
  recountNo: number;
  isUnexpected: boolean;
  hasConflict: boolean;
  conflictStatus: CountConflictStatus;
  conflictQty: string | null;
  movementId: string | null;
  note: string | null;
  /** Per base unit; `null` for a role without `seesCosts`. */
  unitCostBase: string | null;
  valueImpactBase: string | null;
}

export interface CountReviewHeader {
  id: string;
  countNo: string;
  status: CountStatus;
  kind: CountKind;
  isBlind: boolean;
  warehouseId: string | null;
  tolerancePct: string;
  reviewVersion: number;
  startedAt: string | null;
  submittedAt: string | null;
  submittedBy: string | null;
  reviewedAt: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  recountNote: string | null;
  note: string | null;
  /** The `stock_movements` batch the approval wrote. */
  batchId: string | null;
}

export interface CountReviewStats {
  linesTotal: number;
  linesCounted: number;
  linesWithVariance: number;
  linesOverTolerance: number;
  /** Σ variance in base units — not a display number on its own (units differ). */
  netVarianceBase: string;
  /** `null` for a role without `seesCosts`. */
  valueImpactBase: string | null;
}

export interface CountReview {
  count: CountReviewHeader;
  stats: CountReviewStats;
  lines: CountReviewLine[];
}

const obj = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : typeof v === 'number' ? String(v) : null);
const num = (v: unknown): string => (typeof v === 'number' || typeof v === 'string' ? String(v) : '0');
const optNum = (v: unknown): string | null => (v === null || v === undefined ? null : num(v));
const int = (v: unknown): number => (Number.isFinite(Number(v)) ? Number(v) : 0);

function varianceClass(raw: unknown, expected: string, counted: string | null, tolerancePct: string): CountVarianceClass {
  const known = ['equal', 'within_tolerance', 'over_tolerance', 'uncounted'];
  if (typeof raw === 'string' && known.includes(raw)) return raw as CountVarianceClass;
  // a blind session in progress returns null here; classify the same way
  // `private.variance_class` does, so the reviewer sees one rule everywhere
  return classifyVariance(expected, counted, tolerancePct);
}

/** `private.variance_class` in TypeScript — the mobile keypad needs the same answer. */
export function classifyVariance(expected: DecimalInput, counted: DecimalInput | null | undefined, tolerancePct: DecimalInput = 0): CountVarianceClass {
  if (counted === null || counted === undefined || counted === '') return 'uncounted';
  const e = D(expected);
  const c = D(counted);
  const diff = c.minus(e);
  if (diff.isZero()) return 'equal';
  if (e.isZero()) return 'over_tolerance';
  return diff.dividedBy(e).times(100).abs().lessThanOrEqualTo(D(tolerancePct)) ? 'within_tolerance' : 'over_tolerance';
}

/**
 * Tolerant on purpose: the RPC answers with `jsonb`, so the client has no
 * compile-time shape, and a key a later phase adds must not throw the screen
 * away. A payload without a count id is not a review and reads as `null`.
 */
export function parseCountReview(raw: unknown): CountReview | null {
  if (!raw || typeof raw !== 'object') return null;
  const root = obj(raw);
  const head = obj(root.count);
  const id = str(head.id);
  if (!id) return null;
  const tolerancePct = num(head.tolerance_pct ?? 0);
  const stats = obj(root.stats);

  return {
    count: {
      id,
      countNo: str(head.count_no) ?? '',
      status: parseCountStatus(head.status) ?? 'draft',
      kind: (COUNT_KINDS as readonly string[]).includes(str(head.kind) ?? '') ? (head.kind as CountKind) : 'cycle',
      isBlind: head.is_blind === true,
      warehouseId: str(head.warehouse_id),
      tolerancePct,
      reviewVersion: int(head.review_version),
      startedAt: str(head.started_at),
      submittedAt: str(head.submitted_at),
      submittedBy: str(head.submitted_by),
      reviewedAt: str(head.reviewed_at),
      approvedAt: str(head.approved_at),
      approvedBy: str(head.approved_by),
      recountNote: str(head.recount_note),
      note: str(head.note),
      batchId: str(head.batch_id),
    },
    stats: {
      linesTotal: int(stats.lines_total),
      linesCounted: int(stats.lines_counted),
      linesWithVariance: int(stats.lines_with_variance),
      linesOverTolerance: int(stats.lines_over_tolerance),
      netVarianceBase: num(stats.net_variance_base ?? 0),
      valueImpactBase: optNum(stats.value_impact_base),
    },
    lines: arr(root.lines)
      .map((l) => {
        const line = obj(l);
        const expectedQty = num(line.expected_qty ?? 0);
        const countedQty = optNum(line.counted_qty);
        const factor = Number(line.unit_factor ?? 1);
        return {
          id: str(line.id) ?? '',
          itemId: str(line.item_id),
          sku: str(line.sku),
          nameTr: str(line.name_tr) ?? '',
          attributes: line.attributes ?? null,
          lotId: str(line.lot_id),
          lotNo: str(line.lot_no),
          locationId: str(line.location_id),
          locationCode: str(line.location_code) ?? '',
          unit: str(line.unit),
          unitFactor: Number.isFinite(factor) && factor > 0 ? factor : 1,
          expectedQty,
          countedQty,
          // the column is generated (`counted_qty - expected_qty`); an uncounted
          // line has none, and a conflict may have moved `expected_qty` since
          variance: optNum(line.variance) ?? (countedQty === null ? '0' : D(countedQty).minus(D(expectedQty)).toString()),
          variancePct: optNum(line.variance_pct),
          varianceClass: varianceClass(line.variance_class, expectedQty, countedQty, tolerancePct),
          resolution: (['pending', 'adjusted', 'ignored', 'recount'] as string[]).includes(str(line.resolution) ?? '')
            ? (line.resolution as CountLineResolution)
            : 'pending',
          recountNo: int(line.recount_no),
          isUnexpected: line.is_unexpected === true,
          hasConflict: line.has_conflict === true,
          conflictStatus: (['none', 'open', 'kept', 'recount'] as string[]).includes(str(line.conflict_status) ?? '')
            ? (line.conflict_status as CountConflictStatus)
            : 'none',
          conflictQty: optNum(line.conflict_qty),
          movementId: str(line.movement_id),
          note: str(line.note),
          unitCostBase: optNum(line.unit_cost_base),
          valueImpactBase: optNum(line.value_impact_base),
        } satisfies CountReviewLine;
      })
      .filter((line) => !!line.id),
  };
}

/** Default order: location, then the item's SKU — the order the RPC already sends. */
export function sortCountLines(lines: CountReviewLine[]): CountReviewLine[] {
  return [...lines].sort(
    (a, b) => a.locationCode.localeCompare(b.locationCode, 'tr') || (a.sku ?? '').localeCompare(b.sku ?? '', 'tr') || a.nameTr.localeCompare(b.nameTr, 'tr'),
  );
}

// ── quantities ──────────────────────────────────────────────────────────────
export interface CountLineQty {
  /** In the line's own unit. */
  expected: Dec;
  counted: Dec | null;
  variance: Dec;
}

export function countLineQty(line: CountReviewLine): CountLineQty {
  const factor = D(line.unitFactor > 0 ? line.unitFactor : 1);
  return {
    expected: D(line.expectedQty).dividedBy(factor),
    counted: line.countedQty === null ? null : D(line.countedQty).dividedBy(factor),
    variance: D(line.variance).dividedBy(factor),
  };
}

/** `−46` · `+36` · `—` for a line with no difference (§0 numbers rule). */
export function formatCountVariance(value: DecimalInput, opts: { decimals?: number; locale?: 'tr' | 'en' } = {}): string {
  const v = D(value);
  const decimals = opts.decimals ?? 0;
  if (D(v.toFixed(decimals)).isZero()) return '—';
  return formatNumber(v, { decimals, minDecimals: 0, signed: true, locale: opts.locale });
}

/** CSV keeps the display unit but never the Turkish minus (Excel reads it as text). */
export function csvCountQty(value: DecimalInput, decimals = 0): string {
  return formatNumber(value, { decimals, minDecimals: 0 }).replace(MINUS, '-');
}

// ── selection ───────────────────────────────────────────────────────────────
/**
 * A line the reviewer may tick: it has a difference, nothing has been done with
 * it yet, and its conflict (if any) has been resolved. `approve_stock_count`
 * applies the same three rules server side, so a row the screen offers is a row
 * the engine will accept.
 */
export function countLineSelectable(line: CountReviewLine): boolean {
  return !D(line.variance).isZero() && line.resolution === 'pending' && line.conflictStatus !== 'open';
}

/**
 * What is ticked when the review opens (§6.5): every over-tolerance line that is
 * still pending and not in conflict. A small difference inside the tolerance and
 * a line that moved during the count start unticked — the reviewer decides those
 * one by one.
 */
export function defaultCountSelection(lines: CountReviewLine[]): string[] {
  return lines.filter((l) => countLineSelectable(l) && l.varianceClass === 'over_tolerance').map((l) => l.id);
}

/**
 * The selection after the review reloaded: a row that is still selectable keeps
 * what the reviewer did with it, and anything else is dropped (§6.5). A reload
 * that brings a brand-new over-tolerance line does **not** tick it silently —
 * the count changed, and the confirmation must restate what the reviewer chose.
 */
export function keepCountSelection(lines: CountReviewLine[], previous: Iterable<string>): string[] {
  const kept = new Set(previous);
  return lines.filter((l) => countLineSelectable(l) && kept.has(l.id)).map((l) => l.id);
}

// ── what a re-read changed (§6.7 "Stale data") ──────────────────────────────
/**
 * The little the confirmation has to remember about the review it restated, so
 * that a re-read can say what moved. Quantities as the decimal strings the RPC
 * sent, plus the two flags the sheet acted on — comparing the lines themselves
 * would compare costs and notes the sheet never showed.
 */
export interface CountLineSnapshot {
  id: string;
  countedQty: string | null;
  variance: string;
  selected: boolean;
  selectable: boolean;
}

export function countReviewSnapshot(lines: CountReviewLine[], selected: Iterable<string>): CountLineSnapshot[] {
  const ids = new Set(selected);
  return lines.map((l) => ({
    id: l.id,
    countedQty: l.countedQty,
    variance: l.variance,
    selected: ids.has(l.id),
    selectable: countLineSelectable(l),
  }));
}

export interface CountReviewChanges {
  /** Restated lines that are still selected but carry another counted quantity. */
  changed: number;
  /** Restated lines the reload took out of the selection (re-counted, or adjusted elsewhere). */
  dropped: number;
  /** Differences that are selectable now and were not offered before — nobody has ticked them. */
  appeared: number;
  /** False when the version moved but every number the sheet showed stayed the same. */
  any: boolean;
}

/**
 * `approve_stock_count` refuses a stale `review_version`, and the sheet then
 * re-reads the review rather than repeating a refusal it cannot clear. This is
 * what it tells the reviewer changed between the restatement it showed and the
 * review it just read: a quantity that moved, a line that left the selection, a
 * new difference nobody has ticked (§6.7).
 */
export function countReviewChanges(before: CountLineSnapshot[], lines: CountReviewLine[], selected: Iterable<string>): CountReviewChanges {
  const was = new Map(before.map((l) => [l.id, l]));
  const ids = new Set(selected);
  let changed = 0;
  let appeared = 0;
  for (const line of lines) {
    const old = was.get(line.id);
    if (ids.has(line.id)) {
      if (old?.selected && (old.countedQty !== line.countedQty || !D(old.variance).equals(D(line.variance)))) changed += 1;
      continue;
    }
    // a row nobody could tick before — a brand-new line, or one that was equal
    // until the counter wrote it again — is a difference the reviewer has not seen
    if (countLineSelectable(line) && !old?.selectable) appeared += 1;
  }
  const dropped = before.filter((l) => l.selected && !ids.has(l.id)).length;
  return { changed, dropped, appeared, any: changed > 0 || dropped > 0 || appeared > 0 };
}

// ── netting ─────────────────────────────────────────────────────────────────
/** The unit the "koli eşdeğeri" total is expressed in (`units.code`). */
export const CASE_UNIT = 'case';

export interface CountNet {
  /**
   * `case` — the sum is in cases and `excluded` lines were left out;
   * `unit` — no line counts in cases but they all share one unit;
   * `mixed` — the lines have nothing in common, so there is no honest total ("—").
   */
  mode: 'case' | 'unit' | 'mixed';
  net: Dec;
  unit: string | null;
  /** Lines the `case` total had to leave out (Koli boxes, Poşet packs …). */
  excluded: number;
  /** Lines that went into the total. */
  counted: number;
}

/**
 * "Net miktar farkı · koli eşdeğeri" (§6.4) and the footer's "net −62".
 *
 * A session mixes units — napkin cases, cardboard boxes in pieces, bags in packs
 * — so a plain sum would add koli to adet. Only the lines counted in cases go
 * into the case total, and the tile says how many lines it left out (open
 * question §11.4; a later phase can widen this to every item that *has* a case
 * UoM by passing `caseFactor`).
 */
export function countNet(lines: CountReviewLine[], opts: { caseUnit?: string; caseFactor?: (itemId: string | null) => number | null } = {}): CountNet {
  const caseUnit = opts.caseUnit ?? CASE_UNIT;
  const rows = lines.filter((l) => !D(l.variance).isZero());
  if (rows.length === 0) return { mode: 'case', net: D(0), unit: caseUnit, excluded: 0, counted: 0 };

  const inCases = rows.filter((l) => l.unit === caseUnit || (opts.caseFactor?.(l.itemId) ?? null) !== null);
  if (inCases.length > 0) {
    let net = D(0);
    for (const line of inCases) {
      const factor = line.unit === caseUnit ? line.unitFactor : (opts.caseFactor?.(line.itemId) ?? line.unitFactor);
      net = net.plus(D(line.variance).dividedBy(D(factor > 0 ? factor : 1)));
    }
    return { mode: 'case', net, unit: caseUnit, excluded: rows.length - inCases.length, counted: inCases.length };
  }

  const units = new Set(rows.map((l) => l.unit ?? ''));
  if (units.size === 1) {
    let net = D(0);
    for (const line of rows) net = net.plus(countLineQty(line).variance);
    return { mode: 'unit', net, unit: rows[0]!.unit, excluded: 0, counted: rows.length };
  }
  return { mode: 'mixed', net: D(0), unit: null, excluded: rows.length, counted: 0 };
}

export interface CountTotals {
  /** How many lines the totals cover. */
  lines: number;
  net: CountNet;
  /** Σ `value_impact_base`, or `null` when the role may not see costs. */
  valueImpactBase: Dec | null;
}

/**
 * The numbers the footer and the confirmation restate. `selected` limits them to
 * the ticked rows; `null` covers every line with a difference, which is what the
 * stat tiles show.
 */
export function countTotals(lines: CountReviewLine[], selected: Iterable<string> | null = null): CountTotals {
  const ids = selected === null ? null : new Set(selected);
  const rows = lines.filter((l) => (ids === null ? !D(l.variance).isZero() : ids.has(l.id)));
  // one line with a value is enough to total: a masked cost is absent for every
  // line at once (RLS hides the whole sidecar), so `null` means "may not see it".
  // Nothing to total at all is a real zero, not a masked value — a count with no
  // difference shows "0 ₺" to every role (§6.6).
  let value: Dec | null = rows.length === 0 ? D(0) : null;
  for (const line of rows) {
    if (line.valueImpactBase === null) continue;
    value = (value ?? D(0)).plus(D(line.valueImpactBase));
  }
  return { lines: rows.length, net: countNet(rows), valueImpactBase: value };
}

/** The lines an approval will ignore: counted but unselected, plus the uncounted ones (§6.7). */
export interface CountApprovalRest {
  /** Lines with a difference the reviewer did not tick. */
  unselected: number;
  /** Lines nobody counted — the server marks them `ignored`. */
  uncounted: number;
}

export function countApprovalRest(lines: CountReviewLine[], selected: Iterable<string>): CountApprovalRest {
  const ids = new Set(selected);
  return {
    unselected: lines.filter((l) => !D(l.variance).isZero() && l.resolution === 'pending' && !ids.has(l.id)).length,
    uncounted: lines.filter((l) => l.varianceClass === 'uncounted').length,
  };
}

// ── what the reviewer may do ────────────────────────────────────────────────
export type CountApprovalBlock = 'ok' | 'role' | 'status' | 'offline' | 'selfApproval' | 'noSelection';

export interface CountApprovalGateInput {
  status: CountStatus;
  /** `stock_count.submitted_by` — who counted. */
  submittedBy: string | null;
  viewerId: string | null;
  /** `can(role, 'stockApprove')` */
  canApprove: boolean;
  /** `settings.stock.count_self_approval` */
  selfApproval: boolean;
  online?: boolean;
  selectedCount: number;
}

/**
 * Why the "Farkları onayla" button is not available — the same order the server
 * checks in, so the screen never offers a click `approve_stock_count` would
 * refuse (§6.9).
 *
 * `selfApproval` is the company setting, and the database honours nothing else:
 * the owner's rule "I may approve my own count" is that setting turned on
 * (Ayarlar › Stok ve sayım), which is why an owner who counted gets a hint
 * rather than a silent refusal.
 */
export function countApprovalGate(input: CountApprovalGateInput): CountApprovalBlock {
  if (!input.canApprove) return 'role';
  if (input.status !== 'submitted') return 'status';
  if (input.online === false) return 'offline';
  if (input.submittedBy && input.viewerId && input.submittedBy === input.viewerId && !input.selfApproval) return 'selfApproval';
  if (input.selectedCount === 0) return 'noSelection';
  return 'ok';
}

/** "Sayımı kapat" instead of "Farkları onayla": nothing to post (§6.6). */
export function isCountWithoutVariance(lines: CountReviewLine[]): boolean {
  return lines.length > 0 && lines.every((l) => D(l.variance).isZero());
}

/** Lines whose expected quantity was recomputed because stock moved mid-count (§6.2). */
export function countConflictLines(lines: CountReviewLine[]): CountReviewLine[] {
  return lines.filter((l) => l.hasConflict || l.conflictStatus === 'open');
}
